'use client';

import { animated, useTransition } from '@react-spring/web';
import Image, { StaticImageData } from '../image';
import { useEffect, useRef, useState } from 'react';
import { create } from 'zustand';

export interface ICardBoxHashLinkProps {
  icon: string | StaticImageData;
  title: string;
  id: string;
  isSticky?: boolean;
  isHashLinked?: boolean;
  scrollY?: number;
}

export interface ICardBoxListHashLinkProps {
  data: Array<ICardBoxHashLinkProps>;
  scrollY?: number;
  showOriginal?: boolean;
  isHashLinked?: boolean;
  className?: string;
}

export const CardBoxHashLink: React.FC<ICardBoxHashLinkProps> = ({
  id,
  icon,
  title,
  isSticky = false,
  isHashLinked = false,
}) => {
  const hasPointer = id && id !== '';
  const [disable, setDisable] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      setDisable(
        location.hash ? !(isHashLinked && location.hash === `#${id}`) : false
      );
    };
    window.addEventListener('hashchange', handlePopstate);
    return () => window.removeEventListener('hashchange', handlePopstate);
  }, [isHashLinked, id]);

  return (
    <a
      href={isHashLinked ? `#${id}` : id}
      className="flex-1 h-full w-full pointer-events-auto relative"
    >
      <p
        className={`${
          isSticky
            ? 'h-full justify-center'
            : 'bg-blur/[.2] backdrop-blur-[10px] font-500 py-5'
        } rounded-2xl gap-5 flex w-full items-center pl-[30px] mobile:gap-3 mobile:rounded-lg desktop:p-5 mobile:p-5 ${
          hasPointer ? 'cursor-pointer' : 'cursor-default'
        }`}
      >
        <Image
          alt="工具图标"
          src={icon}
          height={isSticky ? 80 : 120}
          width={isSticky ? 80 : 120}
          className={`${
            isSticky ? 'w-8 h-8' : 'h-[60px] w-[60px]'
          } mobile:w-8 mobile:h-8`}
        />
        <span className="text-base text-primary mobile:text-sm line-clamp-1">
          {title}
        </span>
      </p>
      {isSticky && disable && (
        <div className="absolute left-0 top-0 w-full h-full blur-[10px] bg-white/60" />
      )}
    </a>
  );
};

const useCardVisible = create<{ inView: boolean; onTop: boolean }>(() => ({
  inView: true,
  onTop: false,
}));

export const CardBoxList = (props: ICardBoxListHashLinkProps) => {
  const { data = [], isHashLinked = false, className = '' } = props;
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      const inView = entries[0].intersectionRatio > 0;
      const onTop = entries[0].boundingClientRect.top < 0;
      useCardVisible.setState({ inView, onTop });
    });
    if (ref.current) {
      io.observe(ref.current);
      return () => io.disconnect();
    }
  }, []);
  return (
    <div
      className={`flex justify-center w-full h-[120px] bg-gradient-to-b from-white/[0.0] to-white mobile:h-auto ${className}`}
      ref={ref}
    >
      <div className="flex justify-center gap-3 h-full max-w-5xl w-full desktop:mx-5 mobile:grid mobile:grid-cols-2">
        {data.map((m, i) => (
          <CardBoxHashLink
            id={m.id}
            icon={m.icon}
            key={i}
            isSticky={false}
            title={m.title}
            isHashLinked={isHashLinked}
          />
        ))}
      </div>
    </div>
  );
};

export const CardBoxHashLinkList = (props: ICardBoxListHashLinkProps) => {
  const { data = [], showOriginal } = props;
  const { inView, onTop } = useCardVisible((s) => s);
  const show = !inView && onTop;
  const transition = useTransition(show, {
    from: { opacity: 0, transform: `translateY(-50%)` },
    enter: { opacity: 1, transform: `translateY(0%)` },
    leave: { opacity: 0, transform: `translateY(-50%)` },
    config: { duration: 300 },
  });

  return (
    <div>
      {showOriginal && <CardBoxList data={data} isHashLinked={true} />}
      {transition(
        (style, show) =>
          show && (
            <animated.div
              className="flex justify-center w-full mobile:hidden fixed left-0 top-[72px] z-40 h-16 backdrop-blur-md bg-[rgb(243,245,247,0.8)]"
              style={style}
            >
              <div className="flex justify-center gap-x-3 h-full max-w-5xl w-full desktop:px-5 ">
                {data.map((m, i) => (
                  <CardBoxHashLink
                    id={m.id}
                    icon={m.icon}
                    key={i}
                    isSticky={true}
                    title={m.title}
                    isHashLinked={true}
                  />
                ))}
              </div>
            </animated.div>
          )
      )}
    </div>
  );
};
